import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable, Optional } from '@angular/core';
import { Observable, merge, of, catchError, filter, map, mergeMap, take, timeout } from 'rxjs';
import {
  OAuthModuleConfig,
  OAuthResourceServerErrorHandler,
  OAuthService,
} from 'angular-oauth2-oidc';

@Injectable()
export class OAuthInterceptor implements HttpInterceptor {
  private excludedUrls: string[] = ['organizations/subdomains/'];
  constructor(
    private oAuthService: OAuthService,
    private errorHandler: OAuthResourceServerErrorHandler,
    @Optional() private moduleConfig: OAuthModuleConfig,
  ) {}
  private shouldExcludeToken(url: string): boolean {
    return this.excludedUrls.some(excludedUrl => url.includes(excludedUrl));
  }

  private checkUrl(url: string): boolean {
    const { customUrlValidation, allowedUrls } =
      this.moduleConfig?.resourceServer || {};
    if (customUrlValidation) {
      return customUrlValidation(url);
    }
    if (allowedUrls) {
      return !!allowedUrls.find(u =>
        url.toLowerCase().startsWith(u.toLowerCase()),
      );
    }
    return true;
  }
  private handleRequestWithToken(
    req: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    return next
      .handle(req)
      .pipe(catchError(err => this.errorHandler.handleError(err)));
  }
  private getToken(): Observable<string | null> {
    const token$ = of(this.oAuthService.getAccessToken()).pipe(
      filter(token => !!token),
    );
    const tokenReceived$ = this.oAuthService.events.pipe(
      filter(e => e.type === 'token_received'),
      timeout(this.oAuthService.waitForTokenInMsec || 0),
      catchError(() => of(null)), // timeout is not an error
      map(() => this.oAuthService.getAccessToken()),
    );
    return merge(token$, tokenReceived$);
  }
  private addTokenToRequest(
    req: HttpRequest<any>,
    token: string,
  ): HttpRequest<any> {
    const header = 'Bearer ' + token;
    const headers = req.headers.set('Authorization', header);
    return req.clone({ headers });
  }

  public intercept(
    req: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    const url = req.url.toLowerCase();
    if (this.shouldExcludeToken(url)) {
      const modifiedReq = req.clone({
        headers: req.headers.delete('Authorization'),
      });
      return next.handle(modifiedReq);
    }
    if (!this.moduleConfig?.resourceServer || !this.checkUrl(url)) {
      return next.handle(req);
    }
    const sendAccessToken = this.moduleConfig.resourceServer.sendAccessToken;
    if (!sendAccessToken) {
      return this.handleRequestWithToken(req, next);
    }
    return this.getToken().pipe(
      take(1),
      mergeMap(token => {
        if (token) {
          req = this.addTokenToRequest(req, token);
        }
        return this.handleRequestWithToken(req, next);
      }),
    );
  }
}
